import React, { useEffect, useState } from "react";
import styles from "./LeadGeneration.module.css";
import * as XLSX from "xlsx";
import LoaderComp from "../../../LoaderComp";
import { FaAngleDown } from "react-icons/fa";
import { MdOutlineAddToPhotos } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { BiSolidEdit } from "react-icons/bi";
const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const LeadGeneration = ({ appData }) => {
  const [leads, setLeads] = useState([]);
  const [botForms, setBotForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingForms, setLoadingForms] = useState(true);
  const [error, setError] = useState(null);
  const [appName, setAppName] = useState(appData.app_name);
  const [leadPreferences, setLeadPreferences] = useState({});
  const [editingPreference, setEditingPreference] = useState(null);
  const [preferenceLoading, setPreferenceLoading] = useState({});

  // Template type options
  const TEMPLATE_TYPES = {
    THANK_YOU: "THANK_YOU",
    GET_BACK_TO_YOU_SHORTLY: "GET_BACK_TO_YOU_SHORTLY",
  };

  // Template descriptions for display
  const TEMPLATE_DESCRIPTIONS = {
    [TEMPLATE_TYPES.THANK_YOU]:
      "Thank You - Shows appreciation message after form submission",
    [TEMPLATE_TYPES.GET_BACK_TO_YOU_SHORTLY]:
      "Get Back to You Shortly - Informs user about follow-up",
  };

  // UUID mapping for forms - update this based on your actual form UUIDs
  const getFormUuidMapping = (formName) => {
    const uuidMappings = {
      "Contact Sales": "q_7df6khvju", // Map "Contact Sales" form to the correct UUID
      // Add other form mappings as needed:
      // "Other Form Name": "other-uuid-here",
    };
    return uuidMappings[formName] || null;
  };

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found in localStorage");

        const payload = JSON.parse(atob(token.split(".")[1]));
        const tenantId = payload.tenant_id;
        const region = appData.region.toLowerCase();

        const response = await fetch(
          `https://${region}.autobot2.${backend_api_base_url}/get_all_leads?tenant_id=${tenantId}&app_name=${appName}`,
          { headers: { Authorization: token } },
        );

        if (!response.ok)
          throw new Error(`Lead API failed: ${response.status}`);

        const data = await response.json();
        setLeads(data);
      } catch (err) {
        setError(err.message);
        console.error("Failed to fetch leads:", err);
      } finally {
        setLoading(false);
      }
    };

    if (appName) fetchLeads();
  }, [appName]);

  // Fetch Bot Form content
  useEffect(() => {
    const fetchBotContent = async () => {
      try {
        setLoadingForms(true);
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found in localStorage");

        const payload = JSON.parse(atob(token.split(".")[1]));
        const tenantId = payload.tenant_id;
        const region = appData.region.toLowerCase();

        const response = await fetch(
          `https://${region}.autobot2.${backend_api_base_url}/get_bot_content?app_name=${appName}&tenant_id=${tenantId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
          },
        );

        if (!response.ok)
          throw new Error(`Bot content fetch failed: ${response.status}`);

        const data = await response.json();

        // 🌿 Safely extract the bot payload
        const botPayload = data?.bot_payload;
        const faq = botPayload?.FAQ;

        const forms = [];

        // Recursive helper to extract forms and their fields
        const extractForms = (node, level = 0) => {
          if (!node || typeof node !== "object") return;

          // Look for form_type nodes (these are forms)
          if (node.type === "form_type" && node.text) {
            const mappedUuid = getFormUuidMapping(node.text);

            const form = {
              form_name: node.text,
              fields: [],
              original_uuid: node.uuid, // Keep original for debugging
              uuid: mappedUuid || node.uuid || `form-${forms.length + 1}`,
            };

            console.log(`[DEBUG] Form "${node.text}":`, {
              original_uuid: node.uuid,
              mapped_uuid: mappedUuid,
              final_uuid: form.uuid,
            });

            // Extract fields from the fields array
            if (Array.isArray(node.fields)) {
              node.fields.forEach((field) => {
                if (field.type === "field_type") {
                  form.fields.push({
                    label: field.text || "Untitled Field",
                    type: field.fieldType || "text",
                    required: field.required || false,
                    placeholder: field.placeholder || "",
                  });
                }
              });
            }

            forms.push(form);
          }

          // Also check for Multiple Choice questions that might have form_type subQuestions
          if (Array.isArray(node.subQuestions)) {
            node.subQuestions.forEach((child) =>
              extractForms(child, level + 1),
            );
          }

          // Check for Multiple Choice answers that might lead to forms
          if (Array.isArray(node.answers)) {
            node.answers.forEach((answer) => {
              if (answer.subQuestions && Array.isArray(answer.subQuestions)) {
                answer.subQuestions.forEach((child) =>
                  extractForms(child, level + 1),
                );
              }
            });
          }
        };

        extractForms(faq, 0);

        setBotForms(forms);

        // Fetch preferences for all forms using app_name query
        fetchAllFormPreferencesByApp(forms, tenantId);
      } catch (err) {
        console.error("Failed to fetch bot content:", err);
        setBotForms([]);
      } finally {
        setLoadingForms(false);
      }
    };

    if (appName) fetchBotContent();
  }, [appName]);

  // Fetch all preferences for the app at once
  const fetchAllFormPreferencesByApp = async (forms, tenantId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      // Get all preferences for this app
      const region = appData.region.toLowerCase();

      const response = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/get_lead_pp_preferences?tenant_id=${tenantId}&app_name=${appName}`,
        { headers: { Authorization: token } },
      );

      if (response.ok) {
        const allPreferences = await response.json();
        console.log("All preferences for app:", allPreferences);

        // Convert array to object keyed by UUID
        const preferencesMap = {};
        allPreferences.forEach((pref) => {
          if (pref.uuid && pref.template_settings) {
            preferencesMap[pref.uuid] = pref.template_settings;
          }
        });

        setLeadPreferences(preferencesMap);
      } else {
        console.log("No preferences found for app");
        setLeadPreferences({});
      }
    } catch (err) {
      console.error("Failed to fetch form preferences by app:", err);
      setLeadPreferences({});
    }
  };

  // CORRECTED: Save/Update lead preferences with proper HTTP methods
  const saveLeadPreferences = async (formUuid, templateType) => {
    try {
      setPreferenceLoading((prev) => ({ ...prev, [formUuid]: true }));

      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const payload = JSON.parse(atob(token.split(".")[1]));
      const tenantId = payload.tenant_id;

      // Proper template structure
      const templateSettings = {
        post_processing_rule: {
          template_type: templateType,
        },
      };

      console.log("Saving template settings for UUID:", formUuid, {
        tenant_id: tenantId,
        app_name: appName,
        template_settings: templateSettings,
      });

      // Check if preference exists by checking our local state
      const preferenceExists = leadPreferences[formUuid];

      let response;
      const region = appData.region.toLowerCase();

      if (preferenceExists) {
        // CORRECTED: Use PUT method for update

        response = await fetch(
          `https://${region}.autobot2.${backend_api_base_url}/update_lead_pp_preferences`,
          {
            method: "PUT", // Changed from POST to PUT
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({
              tenant_id: tenantId,
              uuid: formUuid,
              new_template_settings: templateSettings,
            }),
          },
        );
      } else {
        // Create new - keep as POST
        response = await fetch(
          `https://${region}.autobot2.${backend_api_base_url}/add_lead_pp_preferences`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({
              tenant_id: tenantId,
              app_name: appName,
              uuid: formUuid,
              template_settings: templateSettings,
            }),
          },
        );
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to save preferences: ${response.status} - ${errorText}`,
        );
      }

      const result = await response.json();
      console.log("Save preference result:", result);

      // Refresh all preferences after save
      await fetchAllFormPreferencesByApp(botForms, tenantId);

      setEditingPreference(null);
      return result;
    } catch (err) {
      console.error("Failed to save lead preferences:", err);
      alert("Failed to save preferences: " + err.message);
    } finally {
      setPreferenceLoading((prev) => ({ ...prev, [formUuid]: false }));
    }
  };

  // Delete lead preferences
  const deleteLeadPreferences = async (formUuid) => {
    if (!window.confirm("Are you sure you want to delete these preferences?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const payload = JSON.parse(atob(token.split(".")[1]));
      const tenantId = payload.tenant_id;
      const region = appData.region.toLowerCase();

      const response = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/delete_lead_pp_preferences?tenant_id=${tenantId}&uuid=${formUuid}`,
        {
          method: "DELETE",
          headers: { Authorization: token },
        },
      );

      if (!response.ok) throw new Error("Failed to delete preferences");

      // Refresh all preferences after delete
      await fetchAllFormPreferencesByApp(botForms, tenantId);
    } catch (err) {
      console.error("Failed to delete lead preferences:", err);
      alert("Failed to delete preferences: " + err.message);
    }
  };

  // Get preference for a specific form
  const getFormPreference = (formUuid) => {
    return leadPreferences[formUuid] || null;
  };

  // Get current template type for display
  const getCurrentTemplateType = (formPreference) => {
    if (!formPreference || !formPreference.post_processing_rule) return null;
    return formPreference.post_processing_rule.template_type;
  };

  const exportLeads = (format = "xlsx") => {
    if (leads.length === 0) return;

    const rows = leads.map((lead) => ({
      "Lead ID": lead.lead_id,
      Timestamp: new Date(lead.timestamp * 1000).toLocaleString(),
      "Lead Data":
        typeof lead.lead_data === "object"
          ? JSON.stringify(lead.lead_data)
          : lead.lead_data,
      Metadata: lead.metadata ? JSON.stringify(lead.metadata) : "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

    XLSX.writeFile(workbook, format === "csv" ? "leads.csv" : "leads.xlsx");
  };

  if (loading) {
    return (
      <div className={styles.leadWrapper}>
        <h2>Lead Generation</h2>
        <div className={styles.loaderContainer}>
          <LoaderComp message="Loading leads..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.leadWrapper}>
        <h2>Lead Generation</h2>
        <p className={styles.error}>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className={styles.leadWrapper}>
      <h2 className={styles.title}>Lead Generation</h2>

      {/* Bot Form Section with Preferences */}
      <div className={styles.botFormSection}>
        <div>
          <h3>Bot Forms & Lead Preferences</h3>
          {loadingForms ? (
            <LoaderComp message="Fetching bot forms..." />
          ) : botForms.length > 0 ? (
            <div className={styles.formsGrid}>
              {botForms.map((form, idx) => {
                const formPreference = getFormPreference(form.uuid);
                const currentTemplateType =
                  getCurrentTemplateType(formPreference);

                return (
                  <div key={idx} className={styles.botFormCard}>
                    {/* ===== Accordion Header (Main Collapse Toggle) ===== */}
                    <div
                      className={styles.cardAccordionHeader}
                      onClick={() =>
                        setBotForms((prev) =>
                          prev.map((f, i2) =>
                            i2 === idx ? { ...f, mainOpen: !f.mainOpen } : f,
                          ),
                        )
                      }
                    >
                      <p>{form.form_name || "Untitled Form"}</p>
                      <span
                        className={`${styles.cardArrow} ${
                          form.mainOpen ? styles.open : ""
                        }`}
                      >
                        <FaAngleDown />
                      </span>
                    </div>

                    {/* ===== Accordion Body ===== */}
                    {form.mainOpen && (
                      <div className={styles.cardAccordionBody}>
                        <div className={styles.formHeader}>
                          <div className={styles.formActions}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingPreference(form.uuid);
                              }}
                              className={styles.editBtn}
                            >
                              {formPreference ? (
                                <BiSolidEdit size={22}/>
                              ) : (
                                <MdOutlineAddToPhotos size={22}/>
                              )}
                            </button>
                            {formPreference && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteLeadPreferences(form.uuid);
                                }}
                                className={styles.deleteBtn}
                              >
                                <RiDeleteBin6Line size={22}/>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Form UUID Display */}
                        <div className={styles.formUuid}>
                          <small>UUID: {form.uuid}</small>
                          {form.original_uuid &&
                            form.original_uuid !== form.uuid && (
                              <small
                                style={{ color: "#dc3545", display: "block" }}
                              >
                                Original: {form.original_uuid}
                              </small>
                            )}
                        </div>

                        {form.fields &&
                        Array.isArray(form.fields) &&
                        form.fields.length > 0 ? (
                          <div className={styles.fieldsList}>
                            <div
                              className={styles.accordionHeader}
                              onClick={() =>
                                setBotForms((prev) =>
                                  prev.map((f, i2) =>
                                    i2 === idx ? { ...f, open: !f.open } : f,
                                  ),
                                )
                              }
                            >
                              <h5>Fields ({form.fields.length})</h5>
                              <span
                                className={`${styles.arrow} ${
                                  form.open ? styles.open : ""
                                }`}
                              >
                                <FaAngleDown />
                              </span>
                            </div>

                            {form.open && (
                              <div className={styles.accordionBody}>
                                <ul>
                                  {form.fields.map((field, i) => (
                                    <li key={i} className={styles.fieldItem}>
                                      <span className={styles.fieldLabel}>
                                        {field.label}
                                      </span>
                                      <span className={styles.fieldType}>
                                        ({field.type})
                                      </span>
                                      {field.required && (
                                        <span className={styles.requiredBadge}>
                                          Required
                                        </span>
                                      )}
                                      {field.placeholder && (
                                        <div className={styles.placeholder}>
                                          Placeholder: "{field.placeholder}"
                                        </div>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className={styles.noFields}>
                            No fields configured
                          </p>
                        )}

                        {/* Lead Preferences */}
                        {editingPreference === form.uuid ? (
                          <div className={styles.preferencesEditor}>
                            <h5>Post-Processing Template</h5>
                            <p className={styles.templateDescription}>
                              Choose how the bot responds after form submission:
                            </p>

                            <div className={styles.templateOptions}>
                              {Object.entries(TEMPLATE_TYPES).map(
                                ([key, value]) => (
                                  <div
                                    key={value}
                                    className={styles.templateOption}
                                  >
                                    <label className={styles.templateLabel}>
                                      <input
                                        type="radio"
                                        name="template_type"
                                        value={value}
                                        defaultChecked={
                                          currentTemplateType === value
                                        }
                                        className={styles.templateRadio}
                                      />
                                      <div className={styles.templateContent}>
                                        <strong className={styles.templateName}>
                                          {key.replace(/_/g, " ")}
                                        </strong>
                                        <p className={styles.templateDesc}>
                                          {TEMPLATE_DESCRIPTIONS[value]}
                                        </p>
                                      </div>
                                    </label>
                                  </div>
                                ),
                              )}
                            </div>

                            <div className={styles.preferenceActions}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingPreference(null);
                                }}
                                className={styles.cancelBtn}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const selectedTemplate =
                                    document.querySelector(
                                      'input[name="template_type"]:checked',
                                    )?.value;
                                  if (selectedTemplate) {
                                    saveLeadPreferences(
                                      form.uuid,
                                      selectedTemplate,
                                    );
                                  } else {
                                    alert("Please select a template type");
                                  }
                                }}
                                disabled={preferenceLoading[form.uuid]}
                                className={styles.saveBtn}
                              >
                                {preferenceLoading[form.uuid]
                                  ? "Saving..."
                                  : "Save Template"}
                              </button>
                            </div>
                          </div>
                        ) : formPreference ? (
                          <div className={styles.preferencesDisplay}>
                            <h5>Current Template:</h5>
                            <div className={styles.templateDisplay}>
                              <div className={styles.templateBadge}>
                                {currentTemplateType?.replace(/_/g, " ") ||
                                  "No template selected"}
                              </div>
                              <div className={styles.templateDescription}>
                                {TEMPLATE_DESCRIPTIONS[currentTemplateType] ||
                                  "No description available"}
                              </div>
                            </div>
                            <div className={styles.preferenceStructure}>
                              <strong>Structure:</strong>
                              <pre className={styles.structureCode}>
                                {JSON.stringify(formPreference, null, 2)}
                              </pre>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.noPreferences}>
                            <p>
                              No post-processing template set. Click "Add
                              Template" to configure.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className={styles.noForms}>No bot forms found</p>
          )}
        </div>
      </div>

      {/* Lead Table Section */}
      {leads.length > 0 && (
        <div className={styles.exportButtons}>
          <button onClick={() => exportLeads("xlsx")}>Export to XLSX</button>
          <button onClick={() => exportLeads("csv")}>Export to CSV</button>
        </div>
      )}

      {leads.length > 0 ? (
        <div className={styles.leadsContainer}>
          <table className={styles.leadsTable}>
            <thead>
              <tr>
                <th>Lead ID</th>
                <th>Timestamp</th>
                <th>Lead Data</th>
                <th>Metadata</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.lead_id}>
                  <td>{lead.lead_id}</td>
                  <td>{new Date(lead.timestamp * 1000).toLocaleString()}</td>
                  <td>
                    <pre>{JSON.stringify(lead.lead_data, null, 2)}</pre>
                  </td>
                  <td>
                    {lead.metadata ? (
                      <pre>{JSON.stringify(lead.metadata, null, 2)}</pre>
                    ) : (
                      "N/A"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>No leads found</p>
      )}
    </div>
  );
};

export default LeadGeneration;
